import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorCode, ErrorSeverity, AppError } from '../../lib/errors';
import { setGlobalNotify, setGlobalDismiss, clearGlobalNotify } from '../../lib/globalNotify';
import { sendToTerminal } from '../../lib/terminalLogger';

export type NotificationType = 'error' | 'warning' | 'info' | 'success';

export interface NotificationAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface NotificationItem {
  id: string;
  type: NotificationType;
  code?: ErrorCode;
  title?: string;
  message: string;
  actions?: NotificationAction[];
  sticky?: boolean;
  createdAt: number;
  meta?: Record<string, any>;
  severity?: ErrorSeverity;
  deduplicationKey?: string; // Chave customizada para deduplicação
  duration?: number; // Duração customizada em ms (sobrescreve padrão)
}

interface NotificationContextValue {
  items: NotificationItem[];
  notify: (n: Omit<NotificationItem, 'id' | 'createdAt'>) => string;
  dismiss: (id?: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({} as NotificationContextValue);

export const useNotifications = () => useContext(NotificationContext);

function generateId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const lastByKey = useRef<Map<string, number>>(new Map());

  const notify: NotificationContextValue['notify'] = useCallback((n) => {
    // Deduplicação melhorada: type + title + message (ou code)
    const dedupKey = n.deduplicationKey || `${n.type}|${n.title ?? ''}|${n.code ?? 'NA'}|${n.message}`;
    const now = Date.now();
    const last = lastByKey.current.get(dedupKey) ?? 0;
    
    // Tempo de deduplicação: 5s para warnings/errors, 3s para success/info
    const dedupDuration = (n.type === 'warning' || n.type === 'error') ? 5000 : 3000;
    
    if (now - last < dedupDuration) {
      return '';
    }
    lastByKey.current.set(dedupKey, now);
    
    // Limpar chaves antigas (mais de 1 minuto)
    if (lastByKey.current.size > 100) {
      const oneMinuteAgo = now - 60000;
      for (const [key, timestamp] of lastByKey.current.entries()) {
        if (timestamp < oneMinuteAgo) {
          lastByKey.current.delete(key);
        }
      }
    }

    const id = generateId();
    const item: NotificationItem = { id, createdAt: now, ...n };
    
    // ✅ DEBUG: Log quando notificação de erro genérico "Ocorreu um Erro" for exibida
    if (n.type === 'error' && (n.title === 'Ocorreu um Erro' || n.message?.includes('Algo inesperado'))) {
      sendToTerminal('🚨 [NOTIFICATION] Erro genérico exibido ao usuário', {
        title: n.title,
        message: n.message,
        code: n.code,
        meta: n.meta,
        stack: new Error().stack?.split('\n').slice(2, 8).join('\n') // Capturar stack parcial
      }, 'error');
    }
    
    setItems((prev) => [item, ...prev].slice(0, 50));
    return id;
  }, []);

  const dismiss: NotificationContextValue['dismiss'] = useCallback((id) => {
    if (!id) {
      setItems((prev) => prev.filter((x) => x.sticky));
      return;
    }
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const clearAll = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({ items, notify, dismiss, clearAll }), [items, notify, dismiss, clearAll]);

  // Inicializar bridge global para código não-React
  useEffect(() => {
    setGlobalNotify(notify);
    setGlobalDismiss(dismiss);
    return () => {
      clearGlobalNotify();
    };
  }, [notify, dismiss]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// API de atalho para integrar com erros (AppError)
export function useNotifyError() {
  const { notify } = useNotifications();
  return useCallback((error: AppError) => {
    notify({
      type: 'error',
      code: error.code,
      title: error.title ?? 'Ocorreu um erro',
      message: error.message,
      meta: error.metadata,
      severity: error.severity,
      sticky: error.severity === 'critical'
    });
  }, [notify]);
}


